// src/hooks/useTenantVocabulary.js
// The option lists this tenant's own data supports.
//
// WHY A HOOK RATHER THAN A FETCH PER COMPONENT
// Four surfaces need the same lists — the contacts filters, the contact editor's
// service lines, the account's Core System dropdown and the training tagger. The
// first two each grew their own fetch, their own null-vs-empty handling and their
// own fallback, which is three chances to get the same distinction wrong.
//
// THE DISTINCTION THAT MATTERS: `null` means NOT ANSWERED YET and `[]` means the
// tenant genuinely has no values for that column. They must not be conflated —
// treating "not loaded" as "empty" hides every dropdown for the first ~200ms, and
// treating "empty" as "not loaded" leaves a control that can only return zero
// rows on screen forever.
//
// WHAT THIS REPLACED
// Hardcoded insurance vocabularies in four components: Guidewire, Duck Creek,
// Majesco, InsuranceNow, Sapiens, Origami as "core systems", and the same vendors
// again as "service lines". For a logistics or professional-services tenant those
// are options that can never match a row, presented as the product's idea of
// their market. Same rule as exavalu_relevance, renamed in migration 0030:
// nothing in the base may carry one customer's market.

import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export default function useTenantVocabulary() {
  // null until answered. See the note above — this is not the same as {}.
  const [options, setOptions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res  = await apiFetch('/api/tenant/vocabulary');
        const data = await res.json().catch(() => ({}));
        // res.ok, not data.success alone: apiFetch RESOLVES on 4xx/5xx, so
        // checking the body only would render a failed request as "this tenant
        // has no values", which is a different and wrong answer.
        if (!cancelled && res.ok && data.success) setOptions(data.options || {});
      } catch {
        // Leave null. Callers fall back to a generic list and the page works; an
        // error here reports a failure the user cannot act on.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return options;
}

/**
 * Pick one list, with a fallback for "not answered yet".
 *
 * An EMPTY derived list is honoured, not replaced: a tenant with no core_system
 * anywhere should see the control hidden or empty rather than be offered another
 * industry's vendors. Only `null` — not yet loaded — falls back.
 */
export function vocabList(options, key, fallback = []) {
  if (options == null) return fallback;
  return Array.isArray(options[key]) ? options[key] : [];
}
